// Angular
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
// RxJS
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
// Object-Path
import * as objectPath from 'object-path';
// Layout
import {
  LayoutConfigService,
  MenuConfigService,
  PageConfigService,
  SplashScreenService,
  SubheaderService,
} from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';
import { LayoutConfig } from '../../../core/_config/layout.config';
import { MenuConfig } from '../../../core/_config/menu.config';
import { PageConfig } from '../../../core/_config/page.config';
// User permissions
import { NgxPermissionsService } from 'ngx-permissions';
import { AuthService, currentUser, currentUserPermissions, Permission } from '../../../core/auth';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import { WorkflowService } from '../../pages/workflow/_services/workflow.service';
import { filter, take } from 'rxjs/operators';
import { LinkGroupService, LinksRequested } from '../../../core/management';
import { FilteredViewService } from '../../../core/services/filtered-view.service';
import { GlobalSettingsService } from '../../../core/services/global-settings.service';

@Component({
  selector: 'kt-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BaseComponent implements OnInit, OnDestroy {
  // Public variables
  suspendAnimations = false;
  selfLayout = 'default';
  asideSelfDisplay: true;
  contentClasses = '';
  contentContainerClasses = '';
  subheaderDisplay = true;
  contentExtended: false;
  filterView: boolean;
  // Private properties
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private currentUserPermissions$: Observable<Permission[]>;
  userLoaded = new BehaviorSubject(false);
  isAgent;

  /**
   * Component constructor
   *
   * param layoutConfigService: LayoutConfigService
   * param menuConfigService: MenuConfigService
   * param pageConfigService: PageConfigService
   * param htmlClassService: HtmlClassService
   * param store
   * param permissionsService
   */
  constructor(
    private router: Router,
    private linkGroupService: LinkGroupService,
    private layoutConfigService: LayoutConfigService,
    private menuConfigService: MenuConfigService,
    private pageConfigService: PageConfigService,
    private htmlClassService: HtmlClassService,
    private store: Store<AppState>,
    private workflowService: WorkflowService,
    private permissionsService: NgxPermissionsService,
    private cdr: ChangeDetectorRef,
    public subheaderService: SubheaderService,
    private filteredViewService: FilteredViewService,
    private authService: AuthService,
    private splashScreenService: SplashScreenService,
    private globalSettingsService: GlobalSettingsService,
    private elementRef: ElementRef,
  ) {
    const element = this.elementRef.nativeElement;
    element.style.zoom = '90%';
    this.loadRolesWithPermissions();
    // register configs by demos
    this.layoutConfigService.loadConfigs(new LayoutConfig().configs);
    this.menuConfigService.loadConfigs(new MenuConfig().configs);
    this.store
      .pipe(
        select(currentUser),
        filter((x) => x.companyID !== undefined && x.companyID !== null),
        take(1),
      )
      .subscribe((res) => {
        this.loadSubMenus();
        this.isAgent = res?.agent;
      });
    this.pageConfigService.loadConfigs(new PageConfig().configs);

    // setup element classes
    this.htmlClassService.setConfig(this.layoutConfigService.getConfig());

    const subscription = this.layoutConfigService.onConfigUpdated$.subscribe((layoutConfig) => {
      // reset body class based on global and page level layout config, refer to html-class.service.ts
      document.body.className = '';
      this.htmlClassService.setConfig(layoutConfig);
    });
    this.unsubscribe.push(subscription);

    this.filteredViewService.isFilterView.subscribe((value) => {
      this.filterView = value;
    });

    this.authService.getUser().subscribe((value) => {
      if (value && value.id) {
        this.userLoaded.next(true);
        this.splashScreenService.hide();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const currentPath = this.router.url.split('#')[0].split('?')[0];
        const nextPath = event.url.split('#')[0].split('?')[0];

        this.suspendAnimations = currentPath === nextPath;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.suspendAnimations = false;
      }
    });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit(): void {
    const config = this.layoutConfigService.getConfig();
    // Load UI from Layout settings
    this.selfLayout = objectPath.get(config, 'self.layout');
    this.asideSelfDisplay = objectPath.get(config, 'aside.self.display');
    this.subheaderDisplay = objectPath.get(config, 'subheader.display');
    this.contentClasses = this.htmlClassService.getClasses('content', true).toString();
    this.contentContainerClasses = this.htmlClassService
      .getClasses('content_container', true)
      .toString();
    this.contentExtended = objectPath.get(config, 'content.extended');

    // let the layout type change
    const subscription = this.layoutConfigService.onConfigUpdated$.subscribe((cfg) => {
      setTimeout(() => {
        this.selfLayout = objectPath.get(cfg, 'self.layout');
      });
    });
    this.unsubscribe.push(subscription);
    this.unsubscribe.push(
      this.subheaderService.breadcrumbs$.subscribe((bc) => {
        if (bc.length > 0) {
          // page: "/data-analysis/page-builder"
          const breadcrumbs = bc[0].page;
          const configUpdate = this.layoutConfigService.getConfig();
          if (breadcrumbs.indexOf('data-analysis') !== -1) {
            objectPath.set(configUpdate, 'subheader.display', false);
            this.subheaderDisplay = objectPath.get(configUpdate, 'subheader.display');
            this.layoutConfigService.setConfig(configUpdate, true);
            return;
          }
          if (!this.subheaderDisplay) {
            objectPath.set(configUpdate, 'subheader.display', true);
            this.subheaderDisplay = objectPath.get(configUpdate, 'subheader.display');
            this.layoutConfigService.setConfig(configUpdate, true);
          }
        }
      }),
    );
    this.globalSettingsService.$loadLinkList.subscribe((res) => {
      if (res) {
        this.loadSubMenus();
      }
    });
    this.store.select(currentUserPermissions);
    this.store.select(currentUser).subscribe(() => {
      this.loadRolesWithPermissions();
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    // https://www.npmjs.com/package/ngx-permissions
    this.permissionsService.flushPermissions();
  }

  /**
   * NGX Permissions, init roles
   */
  loadRolesWithPermissions() {
    this.currentUserPermissions$ = this.store.pipe(select(currentUserPermissions));
    this.unsubscribe.push(
      this.currentUserPermissions$.subscribe((res) => {
        if (!res || res.length === 0) {
          return;
        }

        this.permissionsService.flushPermissions();
        res.forEach((pm: Permission) => this.permissionsService.addPermission(pm.name));
        if (
          res.some(
            (p) =>
              p.name === 'accessToContactModule' ||
              p.name === 'accessToDoctorModule' ||
              p.name === 'accessToPatientModule' ||
              p.name === 'accessToFacilityModule',
          )
        ) {
          this.permissionsService.addPermission('accessToEntityModule');
        }
        if (
          res.some(
            (p) => p.name === 'accessToPharmacyModule' || p.name === 'accessToPharmacyGroupModule',
          )
        ) {
          this.permissionsService.addPermission('accessToPharmacyMenu');
        }
        if (
          res.some((p) => p.name === 'accessToDrugModule' || p.name === 'accessToDrugGroupModule')
        ) {
          this.permissionsService.addPermission('accessToDrugMenu');
        }
        if (
          res.some(
            (p) =>
              p.name === 'accessToAffiliationModule' ||
              p.name === 'accessToAffiliationTypeModule' ||
              p.name === 'accessToClientModule' ||
              p.name === 'accessToInsuranceModule' ||
              p.name === 'accessToCategoryModule' ||
              p.name === 'accessToTagsModule',
          ) ||
          this.permissionsService.getPermission('accessToEntityModule') ||
          this.permissionsService.getPermission('accessToPharmacyMenu') ||
          this.permissionsService.getPermission('accessToDrugMenu')
        ) {
          this.permissionsService.addPermission('accessToListsModule');
        }
        if (
          res.some(
            (p) => p.name === 'accessToRoleModule' || p.name === 'accessToUserManagementModule',
          )
        ) {
          this.permissionsService.addPermission('accessToUserManagementMenu');
        }

        if (
          res.some(
            (p) =>
              p.name === 'accessToAgreementModule' || p.name === 'accessToAgreementTemplateModule',
          )
        ) {
          this.permissionsService.addPermission('accessToAgreementManagementMenu');
        }

        if (res.some((p) => p.name === 'accessDocumentCategory' || p.name === 'accessDocument'))
          this.permissionsService.addPermission('accessToDocumentsManagement');

        if (
          res.some((p) => p.name === 'accessToActivityTemplates' || p.name === 'accessToActivities')
        )
          this.permissionsService.addPermission('accessToActivityTracking');

        if (
          res.some(
            (p) =>
              p.name === 'accessToStatementDashboard' ||
              p.name === 'accessToStatementReview' ||
              p.name === 'accessToStatementTemplatesMapping',
          )
        )
          this.permissionsService.addPermission('accessToStatements');

        if (
          this.permissionsService.getPermission('accessToStatements') ||
          this.permissionsService.getPermission('accessToStatementsCollectionsSettings')
        )
          this.permissionsService.addPermission('accessToStatementsCollections');

        if (
          res.some(
            (p) =>
              p.name === 'accessToOfficeModule' ||
              p.name === 'accessToCompanyModule' ||
              p.name === 'accessToWorkOSModule' ||
              p.name === 'accessToCORSModule',
          ) ||
          this.permissionsService.getPermission('accessToUserManagementMenu') ||
          this.permissionsService.getPermission('accessToDocumentsManagement') ||
          this.permissionsService.getPermission('accessToActivityTracking') ||
          this.permissionsService.getPermission('accessToStatementsCollections') ||
          this.permissionsService.getPermission('accessToLinkModule')
        ) {
          this.permissionsService.addPermission('accessToManagementModule');
        }
        if (
          res.some(
            (p) =>
              p.name === 'accessToWorkflowModule' ||
              p.name === 'accessToApiModule' ||
              p.name === 'accessToActionModule' ||
              p.name === 'accessToEventModule',
          )
        ) {
          this.permissionsService.addPermission('accessToEventsModule');
        }
        if (
          res.some(
            (p) =>
              p.name === 'accessToProgramModule' || p.name === 'accessToProgramParticipantModule',
          )
        ) {
          this.permissionsService.addPermission('accessToProgramMenu');
        }
        if (
          res.some(
            (p) =>
              p.name === 'accessToDeviceModule' ||
              p.name === 'accessToDeviceTypeModule' ||
              p.name === 'accessToDeviceInventoryModule',
          )
        ) {
          this.permissionsService.addPermission('accessToDeviceMenu');
        }
        if (
          res.some(
            (p) =>
              p.name === 'canAddLink' || p.name === 'canEditLink' || p.name === 'canSoftDeleteLink',
          )
        ) {
          this.permissionsService.addPermission('accessToLinkModule');
        }
        if (
          this.permissionsService.getPermission('accessToProgramMenu') ||
          this.permissionsService.getPermission('accessToDeviceMenu')
        ) {
          this.permissionsService.addPermission('accessToRemoteMonitoringModule');
        }
        if (
          this.permissionsService.getPermission('accessToKnostosBuilders') ||
          this.permissionsService.getPermission('accessToPrivateBuilders')
        ) {
          this.permissionsService.addPermission('accessToAnalysisBuilders');
        }

        if (
          res.some(
            (p) =>
              p.name === 'accessComplianceAgencies' ||
              p.name === 'accessCompliancePrograms' ||
              p.name === 'accessComplianceStandards' ||
              p.name === 'accessComplianceReporting',
          )
        )
          this.permissionsService.addPermission('accessCompliance');

        if (
          res.some(
            (p) =>
              p.name === 'accessToDictionaries' ||
              p.name === 'accessToConcepts' ||
              p.name === 'accessToSynonyms',
          )
        )
          this.permissionsService.addPermission('accessToConceptsManagement');

        if (
          res.some(
            (p) =>
              p.name === 'accessToForms' ||
              p.name === 'accessToDocumentTemplates' ||
              p.name === 'accessToFormTemplateMapping' ||
              p.name === 'accessToFormAiAgent',
          )
        )
          this.permissionsService.addPermission('accessToFormsTemplates');

        if (
          res.some(
            (p) =>
              p.name === 'accessToFaxModule' ||
              p.name === 'accessToEmailModule' ||
              p.name === 'accessToInboxManagementModule',
          )
        ) {
          this.permissionsService.addPermission('accessToCommunications');
        }
        localStorage.setItem(
          'currentUserPermissions',
          JSON.stringify(this.permissionsService.getPermissions()),
        );
      }),
    );
  }

  private loadSubMenus() {
    const menuConfig = new MenuConfig();
    if (
      this.permissionsService.getPermission('canViewLinks') ||
      JSON.parse(localStorage.getItem('currentUserPermissions'))[`canViewLinks`]
    ) {
      this.store.dispatch(new LinksRequested());
      this.linkGroupService.getMenuLinkGroups().subscribe((res) => {
        menuConfig.linkGroupMenu.submenu = [];
        res.data.map((linkGroup) => {
          menuConfig.linkGroupMenu.submenu.push({
            title: linkGroup.name,
            bullet: 'dot',
            root: true,
            submenu: linkGroup.links
              ? linkGroup.links.map((element) => {
                  return {
                    title: element.name,
                    translate: '',
                    page: element.type === 'INTERNAL' ? '/links/' + element.id : element.value,
                    type: element.type,
                  };
                })
              : [],
          });
        });
        this.menuConfigService.loadConfigs(menuConfig.configs);
      });
    }
    this.authService.getQRVEYMenu(null, 'USER').subscribe((res) => {
      menuConfig.qrvey.submenu = [];

      if (res.data[0] && res.data[0].apps && res.data[0].apps.length > 0) {
        res.data[0].apps.map((app) => {
          const appMenu = {
            title: `${app.name}`,
            bullet: 'dot',
            submenu: [],
          };
          // Secret param to know if the user CREATOR or No
          let fdv = '';
          const appId = '&appId=' + app.id;
          if (res.data[0] && res.data[0].role === 'CREATOR') {
            fdv = '&fdv=15CHJD';
            const qrveyBuilderApp = {
              title: 'Page Builder',
              page: '/data-analysis/new-page-builder/' + app.qrvey_app_id + '?' + fdv + appId,
            };
            appMenu.submenu.push(qrveyBuilderApp);
          }
          app.page_groups.map((page_groups) => {
            const groupMenu = {
              title: `${page_groups.name}`,
              submenu: [],
            };
            page_groups.pages.map((sub) => {
              let page = {
                title: `${sub.name}`,
                page:
                  '/data-analysis/new-report/' +
                  app.qrvey_app_id +
                  '/' +
                  sub.qrvey_page_id +
                  '?' +
                  fdv +
                  appId,
              };
              if (sub.is_default) {
                const badge = {
                  badge: {
                    value: `${sub.is_default ? 'default' : null}`,
                  },
                };
                page = { ...page, ...badge };
              }
              groupMenu.submenu.push(page);
            });
            appMenu.submenu.push(groupMenu);
          });
          menuConfig.qrvey.submenu.push(appMenu);
          this.menuConfigService.loadConfigs(menuConfig.configs);
        });
      }
    });
  }
}
